import { getDatadogRum } from '@susu/datadog';
import type { LayerDefinition } from '@susu/custom-made/utils/preview/layers';
import type { ConfigurationTypes } from '@susu/custom-made/utils/productDefinition';
import { checkConfigurationTypeRotationAvailable } from '@susu/custom-made/utils/utils';
import type { SyntheticEvent } from 'react';
import React, { useCallback } from 'react';

import { getPreviewLayerClasses } from './previewLayer.classes';
import PreviewRotationLayer from './previewRotationLayer';
import useFabricContext from '@susu/custom-made/providers/hooks/useFabricContext';
import useOptionContext from '@susu/custom-made/providers/hooks/useOptionContext';
import { useFeatureIsOn } from '@susu/feature-flags/react';
import { FEATURE_TOGGLES } from '@susu/custom-made/feature-flags/featureToggles';

const { SHOW_3D_PREVIEW_IMAGE } = FEATURE_TOGGLES;

const PreviewFirstLayer = React.memo(
  ({
    layers,
    fallbackImage,
    isPreviewFailed,
    setIsPreviewFailed,
    configurationType,
    setLayersDone,
  }: Readonly<{
    layers: LayerDefinition[];
    fallbackImage: LayerDefinition;
    isPreviewFailed: boolean;
    setIsPreviewFailed: React.Dispatch<React.SetStateAction<boolean>>;
    configurationType?: ConfigurationTypes;
    setLayersDone?: React.Dispatch<React.SetStateAction<string[]>> | undefined;
  }>) => {
    const { selectedFabric } = useFabricContext();
    const { selectedOptions } = useOptionContext();
    const show3DPreviewImageFeatureEnabled = useFeatureIsOn(SHOW_3D_PREVIEW_IMAGE);

    const onBaseLayerFailed = useCallback(
      (e: SyntheticEvent<HTMLImageElement, Event>) => {
        if (setLayersDone) {
          setLayersDone(prev => [...prev, 'failed']);
        }

        if (!isPreviewFailed) {
          setIsPreviewFailed(true);
          e.currentTarget.src = fallbackImage.src;
          e.currentTarget.srcset = fallbackImage.srcset;
          e.currentTarget.setAttribute('fallback', fallbackImage.src);

          void getDatadogRum().then((rum) => {
            if (!rum.getInitConfiguration()) return;
            rum.addAction('preview-failed', {
              type: 'info',
              eventDetails: {
                time: new Date().toString(),
                selectedFabric: selectedFabric?.name,
                selectedOptions: selectedOptions,
              },
            });
          });
        }
      },
      [fallbackImage, isPreviewFailed],
    );

    const onBaseLayerLoaded = useCallback(
      (e: SyntheticEvent<HTMLImageElement, Event>) => {
        if (setLayersDone) {
          setLayersDone(prev => [...prev, 'done']);
        }

        if (e.currentTarget.getAttribute('fallback') === e.currentTarget.src) {
          return;
        }

        setIsPreviewFailed(false);
      },
      [],
    );

    return (
      <>
        {checkConfigurationTypeRotationAvailable(
          configurationType,
          show3DPreviewImageFeatureEnabled,
        ) && (
          <PreviewRotationLayer
            layer={layers[0]}
            index={0}
            keyPrefix="baselayer"
            onLayerFailed={onBaseLayerFailed}
            onLayerLoaded={onBaseLayerLoaded}
          />
        )}
        {!checkConfigurationTypeRotationAvailable(
          configurationType,
          show3DPreviewImageFeatureEnabled,
        ) && (
          <div className={getPreviewLayerClasses(true)}>
            <img
              src={layers[0].src}
              srcSet={layers[0].srcset}
              onError={onBaseLayerFailed}
              onLoad={onBaseLayerLoaded}
              alt="baselayer"
              sizes={layers[0].sizes}
              fetchPriority="high"
              loading="eager"
            />
          </div>
        )}
      </>
    );
  },
);

PreviewFirstLayer.displayName = 'PreviewFirstLayer';

export default PreviewFirstLayer;
